import {
  Box,
  Step,
  StepButton,
  StepIconProps,
  StepLabel,
  Typography,
} from "@mui/material";
import {
  Card,
  ContentTitle,
  PageLoader,
  IndicationProposalInformations,
} from "~/components";
import { useIndicationFlow, usePartner } from "~/contexts";
import { Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useCallback, useEffect, useMemo } from "react";
import {
  ContractTermOfAdhesionModel,
  ContractTermOfManagementModel,
  TermOfAdhesionModel,
} from "~/graphql/types";
import { BasicData } from "./components/BasicData";
import { Address } from "./components/Address";
import { Documents } from "./components/Documents";
import { LegalRepresentativies } from "./components/LegalRepresentativies";
import { CustomIconContainer, StyledStepper } from "./styles";
import { Check, ErrorOutline } from "@mui/icons-material";
import { isStepCompleted } from "./components/utils";
import { ClientSignature } from "../ProposalFlow/components";

export const ContractFlow: React.FC = () => {
  const { myPartner } = usePartner();
  const {
    stepper,
    goToPage,
    getIndcationAndProposalIsLoading,
    selectedContract,
    selectedIndication,
    selectedProposal,
    refetchProposalAndIndication,
    selectedCustomer,
  } = useIndicationFlow();

  const isPfContract = useMemo(
    () => selectedContract?.customerRef.personType === "pf",
    [selectedContract],
  );

  const renderStepperIndication = useCallback(() => {
    if (stepper === 0) {
      return <BasicData />;
    }

    if (stepper === 1) {
      return <Address />;
    }

    if (isPfContract) {
      if (stepper === 2) {
        return <Documents />;
      }
      if (stepper === 3) {
        return (
          <ClientSignature
            adhesionTerm={
              selectedProposal?.termOfAdhesion as TermOfAdhesionModel
            }
          />
        );
      }
    } else {
      if (stepper === 2) {
        return <LegalRepresentativies />;
      }
      if (stepper === 3) {
        return <Documents />;
      }
      if (stepper === 4) {
        return (
          <ClientSignature
            adhesionTerm={
              selectedProposal?.termOfAdhesion as TermOfAdhesionModel
            }
          />
        );
      }
    }
  }, [stepper, isPfContract, selectedContract?.termOfAdhesion]);

  const steps = isPfContract
    ? ["Dados básicos", "Endereço", "Documentos", "Assinatura do cliente"]
    : [
        "Dados básicos",
        "Endereço",
        "Representantes",
        "Documentos",
        "Assinatura do cliente",
      ];

  const customerPendencies = selectedContract?.customerRef.pendencies;

  const basicDataPendency =
    Boolean(customerPendencies?.name?.rejectedAt) ||
    Boolean(customerPendencies?.email?.rejectedAt) ||
    Boolean(customerPendencies?.phone?.rejectedAt) ||
    Boolean(customerPendencies?.uf.rejectedAt);

  const addressPendency =
    Boolean(customerPendencies?.zipCode?.rejectedAt) ||
    Boolean(customerPendencies?.state?.rejectedAt) ||
    Boolean(customerPendencies?.city?.rejectedAt) ||
    Boolean(customerPendencies?.neighborhood?.rejectedAt) ||
    Boolean(customerPendencies?.street?.rejectedAt) ||
    Boolean(customerPendencies?.number?.rejectedAt);

  const documentPendency = Boolean(
    selectedContract?.files?.some((item) => item.rejectedAt),
  );

  const legalRepresentativyPendency =
    selectedContract?.customerRef?.legalRepresentatives?.some((item) => {
      const hasPendency =
        Boolean(item.pendencies?.name?.rejectedAt) ||
        Boolean(item.pendencies?.document?.rejectedAt) ||
        Boolean(item.pendencies?.email?.rejectedAt) ||
        Boolean(item.pendencies?.phone?.rejectedAt) ||
        Boolean(item.pendencies?.zipCode?.rejectedAt) ||
        Boolean(item.pendencies?.state?.rejectedAt) ||
        Boolean(item.pendencies?.city?.rejectedAt) ||
        Boolean(item.pendencies?.neighborhood?.rejectedAt) ||
        Boolean(item.pendencies?.number?.rejectedAt) ||
        Boolean(item.files?.some((file) => file.rejectedAt));
      return hasPendency;
    });

  const hasStepPendencies = isPfContract
    ? [basicDataPendency, addressPendency, documentPendency]
    : [
        basicDataPendency,
        addressPendency,
        legalRepresentativyPendency,
        documentPendency,
      ];

  const shouldDisableStep = (label: string) => {
    if (!selectedContract) {
      return label !== "Dados básicos";
    }
    return false;
  };

  const CustomStepIcon = (props: StepIconProps) => {
    return (
      <CustomIconContainer
        active={!!props.active}
        completed={!!props.completed}
        error={props.error}
      >
        {props.error && <ErrorOutline color="error" />}
        {!props.error && props.completed && (
          <Check fontSize="small" sx={{ color: "#3BA1FF" }} />
        )}
        {!props.error && !props.completed && (
          <Typography color={props.active ? "white.main" : "#CDCDCD"}>
            {props.icon}
          </Typography>
        )}
      </CustomIconContainer>
    );
  };

  useEffect(() => {
    refetchProposalAndIndication();
  }, [stepper]);

  if (!myPartner?.configuration.allowManualProposal) {
    toast.error(
      "Você não tem  permissão para acessar esta página, entre em contato com seu parceiro para habilitar esta função!",
    );
    return <Navigate to="/app/" />;
  }

  if (getIndcationAndProposalIsLoading) {
    return <PageLoader />;
  }

  return (
    <>
      <Box sx={{ minHeight: "100vh", height: "100%" }}>
        <Card gap={5}>
          <ContentTitle
            title="Detalhes do contrato"
            style={{ padding: 0 }}
            description="Consulte os dados do contrato e pendências"
            breadcrumbLinks={{
              currentLink: "Contrato",
              links: [{ href: "/app/indications", label: "Indicações" }],
            }}
            rightContent={
              selectedProposal && selectedIndication?.status ? (
                <IndicationProposalInformations
                  indicationStatus={selectedIndication?.status}
                  proposal={selectedProposal}
                />
              ) : (
                <></>
              )
            }
          />

          <StyledStepper nonLinear activeStep={stepper}>
            {steps.map((label, index) => (
              <Step
                completed={isStepCompleted(
                  label,
                  selectedContract,
                  selectedCustomer,
                )}
                key={label}
              >
                <StepButton
                  disabled={shouldDisableStep(label)}
                  onClick={() => goToPage(index)}
                >
                  <StepLabel
                    error={hasStepPendencies[index]}
                    slots={{ stepIcon: CustomStepIcon }}
                  >
                    <Typography sx={{ whiteSpace: "nowrap" }}>
                      {label}
                    </Typography>
                  </StepLabel>
                </StepButton>
              </Step>
            ))}
          </StyledStepper>

          {renderStepperIndication()}
        </Card>
      </Box>
    </>
  );
};
